'use client';

import { useEffect, useRef, useState } from 'react';
import { Loader2, MoreHorizontal, X, CheckCircle2, Info, AlertTriangle, XCircle } from 'lucide-react';
import type { ToolbarAction, DocumentPreviewPanelProps, BannerState, BannerTone } from './types';

type Props = {
  document: any;
  actions: ToolbarAction[];
  onClose: () => void;
  getDocumentTitle?: DocumentPreviewPanelProps['getDocumentTitle'];
  getDocumentSubtitle?: DocumentPreviewPanelProps['getDocumentSubtitle'];
  getDocumentStatus?: DocumentPreviewPanelProps['getDocumentStatus'];
  banner?: BannerState | null;
  onBannerDismiss?: () => void;
  viewSwitcher?: DocumentPreviewPanelProps['viewSwitcher'];
};

const STATUS_TONE = {
  emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400',
  amber: 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400',
  red: 'bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-400',
  gray: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
  blue: 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400',
} as const;

const STATUS_DOT = {
  emerald: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  gray: 'bg-neutral-400',
  blue: 'bg-blue-500',
} as const;

const STATUS_MAP: Record<string, { label: string; tone: keyof typeof STATUS_TONE }> = {
  draft: { label: 'Ciornă', tone: 'gray' },
  issued: { label: 'Emisă', tone: 'emerald' },
  paid: { label: 'Plătită', tone: 'emerald' },
  partially_paid: { label: 'Parțial', tone: 'amber' },
  cancelled: { label: 'Anulată', tone: 'gray' },
  storno: { label: 'Storno', tone: 'red' },
  validated: { label: 'Validat', tone: 'emerald' },
};

function defaultTitle(doc: any): string {
  if (!doc) return '';
  const series = doc.series || '';
  const num = doc.number ? String(doc.number).padStart(5, '0') : '';
  return (series + num).trim() || 'Document';
}

function defaultSubtitle(doc: any): string {
  return doc?.client?.name || '';
}

function defaultStatus(doc: any): { label: string; tone: keyof typeof STATUS_TONE } {
  const remaining = doc ? Number(doc.total || 0) - Number(doc.paidAmount || 0) : 0;
  if (doc?.paidAmount > 0 && remaining > 0) return STATUS_MAP.partially_paid!;
  const s = STATUS_MAP[doc?.status as string];
  return s || { label: (doc?.status as string) || '—', tone: 'gray' as const };
}

const BANNER_PILL: Record<BannerTone, { wrap: string; iconColor: string; closeBtn: string; Icon: typeof CheckCircle2 }> = {
  success: {
    wrap: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    closeBtn: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400',
    Icon: CheckCircle2,
  },
  info: {
    wrap: 'bg-primary-50 text-primary-800 dark:bg-primary-500/[0.12] dark:text-primary-300',
    iconColor: 'text-primary-600 dark:text-primary-400',
    closeBtn: 'hover:bg-primary-100 dark:hover:bg-primary-900/50 text-primary-700 dark:text-primary-400',
    Icon: Info,
  },
  warning: {
    wrap: 'bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-300',
    iconColor: 'text-amber-600 dark:text-amber-400',
    closeBtn: 'hover:bg-amber-100 dark:hover:bg-amber-900/50 text-amber-700 dark:text-amber-400',
    Icon: AlertTriangle,
  },
  danger: {
    wrap: 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-300',
    iconColor: 'text-red-600 dark:text-red-400',
    closeBtn: 'hover:bg-red-100 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400',
    Icon: XCircle,
  },
};

export function Toolbar({
  document: doc,
  actions,
  onClose,
  getDocumentTitle = defaultTitle,
  getDocumentSubtitle = defaultSubtitle,
  getDocumentStatus = defaultStatus,
  banner = null,
  onBannerDismiss,
  viewSwitcher,
}: Props) {
  const status = getDocumentStatus(doc);
  const title = getDocumentTitle(doc);
  const subtitle = getDocumentSubtitle(doc);

  const primary = actions.find((a) => a.type === 'primary' && a.show !== false);
  const secondary = actions.find((a) => a.type === 'secondary' && a.show !== false);
  const iconActions = actions.filter((a) => a.type === 'icon' && a.show !== false);
  const moreActions = actions.filter((a) => a.type === 'more-menu-item' && a.show !== false);

  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!moreOpen) return;
    function onClick(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    }
    window.document.addEventListener('mousedown', onClick);
    return () => window.document.removeEventListener('mousedown', onClick);
  }, [moreOpen]);

  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 min-h-[60px]">
      {/* Identificare */}
      <span
        className={
          'inline-flex items-center gap-1.5 text-[0.6875rem] font-semibold px-2.5 py-0.5 rounded-full ' +
          STATUS_TONE[status.tone]
        }
      >
        <span
          className={
            'h-1.5 w-1.5 rounded-full ' + STATUS_DOT[status.tone] +
            // Pulse pe statusurile critice (red/amber) pentru a atrage atenția
            (status.tone === 'red' || status.tone === 'amber' ? ' animate-[pulse_2s_ease-in-out_infinite]' : '')
          }
        />
        {status.label}
      </span>
      {title && (
        <span className="font-mono text-[0.8125rem] font-bold text-neutral-900 dark:text-neutral-100">{title}</span>
      )}
      {subtitle && (
        <>
          <span className="text-neutral-300 dark:text-neutral-700">|</span>
          <span
            className="text-[0.7813rem] text-neutral-600 dark:text-neutral-400 truncate max-w-[480px]"
            title={subtitle}
          >
            {subtitle}
          </span>
        </>
      )}

      {/* Comutator segmentat de variante (ex. Recepție ⇄ e-Factura). Opt-in:
          absent → toolbar-ul rămâne identic pentru toate celelalte previewuri. */}
      {viewSwitcher && viewSwitcher.options.length > 1 && (
        <div className="ml-2 inline-flex items-center gap-0.5 rounded-lg bg-neutral-100 dark:bg-neutral-800 p-0.5">
          {viewSwitcher.options.map((opt) => {
            const active = opt.key === viewSwitcher.activeKey;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => { if (!active) viewSwitcher.onChange(opt.key); }}
                aria-pressed={active}
                data-tooltip={opt.label}
                className={
                  'flex flex-col items-center justify-center gap-0.5 min-w-[56px] h-[48px] px-2 rounded-md transition-colors ' +
                  (active
                    ? 'bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 shadow-sm'
                    : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200')
                }
              >
                {opt.icon && <opt.icon className="h-4 w-4" aria-hidden="true" />}
                <span className="text-[0.625rem] font-medium leading-none whitespace-nowrap">{opt.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Banner pill — confirmare contextuală în header (înlocuiește toast.success).
          Apare după identificarea documentului, auto-dismiss 3s, dismissable manual.
          Layout-ul rămâne neschimbat când lipsește (flex-1 absoarbe spațiul). */}
      {banner && (() => {
        const t = BANNER_PILL[banner.tone] || BANNER_PILL.success;
        const { Icon } = t;
        return (
          <span
            key={String(banner.id ?? banner.label)}
            role="status"
            aria-live="polite"
            className={
              'ml-1.5 inline-flex items-center gap-1.5 max-w-[300px] px-2.5 py-0.5 rounded-full text-[0.7188rem] font-medium animate-slide-down ' +
              t.wrap
            }
          >
            <Icon className={'h-3 w-3 shrink-0 ' + t.iconColor} aria-hidden="true" />
            <span className="truncate">{banner.label}</span>
            <button
              type="button"
              onClick={onBannerDismiss}
              aria-label="Închide notificarea"
              className={'ml-0.5 -mr-1 h-3.5 w-3.5 shrink-0 rounded grid place-items-center transition-colors ' + t.closeBtn}
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        );
      })()}

      <div className="flex-1" />

      {/* Primary */}
      {primary && (primary.stacked ? (
        <button
          onClick={primary.onClick}
          disabled={primary.disabled || primary.loading}
          data-tooltip={primary.tooltip || primary.label}
          className="flex flex-col items-center justify-center gap-0.5 min-w-[56px] h-[52px] px-2 rounded-lg bg-primary text-white hover:bg-primary-700 transition-colors disabled:opacity-50"
        >
          {primary.loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <primary.icon className="h-4 w-4" />
          )}
          <span className="text-[0.625rem] font-semibold leading-none whitespace-nowrap">{primary.label}</span>
        </button>
      ) : (
        <button
          onClick={primary.onClick}
          disabled={primary.disabled || primary.loading}
          data-tooltip={primary.tooltip}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-primary text-white text-[0.7813rem] font-semibold hover:bg-primary-700 active:translate-y-px transition-all disabled:opacity-50"
        >
          {primary.loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <primary.icon className="h-3.5 w-3.5" />
          )}
          {primary.label}
        </button>
      ))}

      {/* Secondary (outline) */}
      {secondary && (secondary.stacked ? (
        <button
          onClick={secondary.onClick}
          disabled={secondary.disabled || secondary.loading}
          data-tooltip={secondary.tooltip || secondary.label}
          className="flex flex-col items-center justify-center gap-0.5 min-w-[56px] h-[52px] px-2 rounded-lg text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors disabled:opacity-50"
        >
          {secondary.loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <secondary.icon className="h-4 w-4" />
          )}
          <span className="text-[0.625rem] font-medium leading-none whitespace-nowrap">{secondary.label}</span>
        </button>
      ) : (
        <button
          onClick={secondary.onClick}
          disabled={secondary.disabled || secondary.loading}
          data-tooltip={secondary.tooltip}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-primary text-primary text-[0.7813rem] font-semibold hover:bg-primary-50 dark:hover:bg-primary-500/[0.12] active:translate-y-px transition-all disabled:opacity-50"
        >
          {secondary.loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <secondary.icon className="h-3.5 w-3.5" />
          )}
          {secondary.label}
        </button>
      ))}

      {/* Separator */}
      {primary && iconActions.length > 0 && (
        <div className="w-px h-5 bg-neutral-200 dark:bg-neutral-700" />
      )}

      {/* Icon actions — stacked layout (icon top + label below) for clarity.
          Same pattern as v-btn-stacked from page headers. */}
      {iconActions.map((a) => (
        <button
          key={a.id}
          onClick={a.onClick}
          disabled={a.disabled || a.loading}
          data-tooltip={a.tooltip || a.label}
          className={
            'flex flex-col items-center justify-center gap-0.5 min-w-[56px] h-[52px] px-2 rounded-lg transition-colors disabled:opacity-50 ' +
            (a.variant === 'danger'
              ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40'
              : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100')
          }
        >
          {a.loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <a.icon className="h-4 w-4" />
          )}
          <span className="text-[0.625rem] font-medium leading-none whitespace-nowrap">{a.label}</span>
        </button>
      ))}

      {/* More menu */}
      {moreActions.length > 0 && (
        <div ref={moreRef} className="relative">
          <button
            onClick={() => setMoreOpen((o) => !o)}
            data-tooltip="Mai multe acțiuni"
            className="flex flex-col items-center justify-center gap-0.5 min-w-[56px] h-[52px] px-2 rounded-lg text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
          >
            <MoreHorizontal className="h-4 w-4" />
            <span className="text-[0.625rem] font-medium leading-none whitespace-nowrap">Mai multe</span>
          </button>
          {moreOpen && (
            <div className="absolute right-0 top-full mt-1.5 min-w-[200px] rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-lg py-1 z-50">
              {moreActions.map((a) => (
                <button
                  key={a.id}
                  onClick={() => {
                    setMoreOpen(false);
                    a.onClick();
                  }}
                  disabled={a.disabled || a.loading}
                  className={
                    'w-full flex items-center gap-2.5 px-3 py-2 text-[0.7813rem] font-medium text-left transition-colors disabled:opacity-50 ' +
                    (a.variant === 'danger'
                      ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40'
                      : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800')
                  }
                >
                  <a.icon className="h-3.5 w-3.5 shrink-0" />
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Separator + Close */}
      <div className="w-px h-5 bg-neutral-200 dark:bg-neutral-700" />
      <button
        onClick={onClose}
        data-tooltip="Închide (Esc)"
        className="w-8 h-8 rounded-lg grid place-items-center text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
